import React, { useState, useEffect } from 'react';
import { ArrowLeft, Bell, BellOff, Clock, Save, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
// import { useAuth } from '../hooks/useAuth'; // Currently unused but may be needed for future features
import { NotificationSettings } from '../types/notification';
import notificationService from '../services/notificationService';
import { showSuccessToast, showErrorToast } from '../utils/toast';

const NotificationSettingsPage: React.FC = () => {
    const navigate = useNavigate();
    // const { user } = useAuth(); // Currently unused but may be needed for future features
    const [settings, setSettings] = useState<NotificationSettings | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [permissionStatus, setPermissionStatus] = useState<'granted' | 'denied' | 'default'>('default');

    // Load notification settings
    const loadSettings = async () => {
        try {
            const userSettings = await notificationService.getNotificationSettings();
            setSettings(userSettings);
        } catch (error) {
            console.error('Failed to load notification settings:', error);
            showErrorToast('Failed to load notification settings');
        } finally {
            setLoading(false);
        }
    };

    // Check notification permission
    const checkPermission = () => {
        const status = notificationService.getPermissionStatus();
        setPermissionStatus(status.granted ? 'granted' : status.denied ? 'denied' : 'default');
    };

    // Request notification permission
    const requestPermission = async () => {
        try {
            const result = await notificationService.requestPermission();
            setPermissionStatus(result.granted ? 'granted' : result.denied ? 'denied' : 'default');
            
            if (result.granted) {
                showSuccessToast('Notification permission granted!');
                // Subscribe to push notifications
                await notificationService.subscribeToPushNotifications();
            } else if (result.denied) {
                showErrorToast('Notification permission denied. You can enable it in your browser settings.');
            }
        } catch (error) {
            console.error('Failed to request permission:', error);
            showErrorToast('Failed to request notification permission');
        }
    };

    // Save settings
    const saveSettings = async () => {
        if (!settings) return;

        setSaving(true);
        try {
            await notificationService.updateNotificationSettings(settings);
            showSuccessToast('Notification settings saved successfully!');
        } catch (error) {
            console.error('Failed to save notification settings:', error);
            showErrorToast('Failed to save notification settings');
        } finally {
            setSaving(false);
        }
    };

    // Update setting
    const updateSetting = (key: keyof NotificationSettings, value: any) => {
        if (!settings) return;
        setSettings({ ...settings, [key]: value });
    };

    // Load settings on mount
    useEffect(() => {
        loadSettings();
        checkPermission();
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="w-8 h-8 border-4 border-gray-200 dark:border-gray-700 border-t-blue-500 dark:border-t-blue-400 rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!settings) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                        Failed to Load Settings
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                        Unable to load your notification settings. Please try again.
                    </p>
                    <button
                        onClick={loadSettings}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8 max-w-2xl">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
                <button
                    onClick={() => navigate(-1)}
                    className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Notification Settings
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        Manage your notification preferences
                    </p>
                </div>
            </div>

            {/* Permission Status */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        {permissionStatus === 'granted' ? (
                            <Bell className="w-5 h-5 text-green-500" />
                        ) : (
                            <BellOff className="w-5 h-5 text-red-500" />
                        )}
                        <div>
                            <h3 className="font-medium text-gray-900 dark:text-white">
                                Browser Notifications
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                {permissionStatus === 'granted' 
                                    ? 'Notifications are enabled'
                                    : permissionStatus === 'denied'
                                    ? 'Notifications are blocked'
                                    : 'Notifications permission not requested'
                                }
                            </p>
                        </div>
                    </div>
                    {permissionStatus !== 'granted' && (
                        <button
                            onClick={requestPermission}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                        >
                            Enable Notifications
                        </button>
                    )}
                </div>
            </div>

            {/* Settings Form */}
            <div className="space-y-6">
                {/* Push Notifications */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Push Notifications
                    </h3>
                    <div className="space-y-4">
                        <label className="flex items-center justify-between">
                            <span className="text-gray-700 dark:text-gray-300">
                                Enable push notifications
                            </span>
                            <input
                                type="checkbox"
                                checked={settings.pushNotificationsEnabled}
                                onChange={(e) => updateSetting('pushNotificationsEnabled', e.target.checked)}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            />
                        </label>
                    </div>
                </div>

                {/* Reminder Settings */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Reminder Settings
                    </h3>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Remind about pending job cards after (hours)
                            </label>
                            <div className="flex items-center gap-3">
                                <Clock className="w-4 h-4 text-gray-500" />
                                <input
                                    type="number"
                                    min="1"
                                    max="168"
                                    value={settings.reminderHours}
                                    onChange={(e) => updateSetting('reminderHours', parseInt(e.target.value) || 1)}
                                    className="w-20 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                    hours
                                </span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                You'll receive reminders for job cards that have been pending for this duration
                            </p>
                        </div>
                    </div>
                </div>

                {/* Notification Types */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Notification Types
                    </h3>
                    <div className="space-y-4">
                        <label className="flex items-center justify-between">
                            <div>
                                <span className="text-gray-700 dark:text-gray-300">
                                    New job card assigned
                                </span>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Get notified when a new job card is assigned to you
                                </p>
                            </div>
                            <input
                                type="checkbox"
                                checked={settings.jobCardAssigned}
                                onChange={(e) => updateSetting('jobCardAssigned', e.target.checked)}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            />
                        </label>

                        <label className="flex items-center justify-between">
                            <div>
                                <span className="text-gray-700 dark:text-gray-300">
                                    Job card reminders
                                </span>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Get reminded about pending job cards
                                </p>
                            </div>
                            <input
                                type="checkbox"
                                checked={settings.jobCardReminders}
                                onChange={(e) => updateSetting('jobCardReminders', e.target.checked)}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            />
                        </label>

                        <label className="flex items-center justify-between">
                            <div>
                                <span className="text-gray-700 dark:text-gray-300">
                                    Task completed
                                </span>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Get notified when tasks are completed
                                </p>
                            </div>
                            <input
                                type="checkbox"
                                checked={settings.taskCompleted}
                                onChange={(e) => updateSetting('taskCompleted', e.target.checked)}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            />
                        </label>

                        <label className="flex items-center justify-between">
                            <div>
                                <span className="text-gray-700 dark:text-gray-300">
                                    Job card closed
                                </span>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Get notified when job cards are closed
                                </p>
                            </div>
                            <input
                                type="checkbox"
                                checked={settings.jobCardClosed}
                                onChange={(e) => updateSetting('jobCardClosed', e.target.checked)}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            />
                        </label>

                        <label className="flex items-center justify-between">
                            <div>
                                <span className="text-gray-700 dark:text-gray-300">
                                    System announcements
                                </span>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Get notified about system updates and announcements
                                </p>
                            </div>
                            <input
                                type="checkbox"
                                checked={settings.systemAnnouncements}
                                onChange={(e) => updateSetting('systemAnnouncements', e.target.checked)}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            />
                        </label>
                    </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end">
                    <button
                        onClick={saveSettings}
                        disabled={saving}
                        className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <Save className="w-4 h-4" />
                        {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NotificationSettingsPage;
